import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Autocomplete, Box, Grid, Stack, TextField, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Icon } from '@iconify/react/dist/iconify.js';
import dayjs from 'dayjs';

import GlobalWrapper from '../../../components/Wrapper/GlobalWrapper';
import GlobalTable from '../../../components/CustomTableWithPagination/GlobalTable';
import useResponsive from '../../../Hooks/useResponsive';
import {
  buildSkillxAttendanceHead,
  CODE_META,
} from '../../../components/datagrid/skillx/skillxAttendanceTableHead';
import {
  getSkillxAttendanceBatches,
  getSkillxAttendanceRegister,
} from '../../../redux/features/skillxSlice';
import { SX } from '../leaderboard/leaderboardData';

/**
 * IntervalSkillX — Attendance Register (CRO).
 *
 * The month grid, in the shape of HR's "All Attendance": one row per learner,
 * one narrow column per day. Pick a batch, pick a year and month.
 *
 * READ ONLY. Class days are owned by the batch calendar screens; nothing here
 * writes attendance, and no scanner record is ever edited.
 *
 * SCOPE. The batch list is whatever `course_batch.cro_assigned` says is yours,
 * or the whole project for a SkillX department user. Settled on the server from
 * the token — this page cannot widen it by asking.
 *
 * WHY A ROW MIGHT BE ALL "?" — that learner has no attendance source: no SkillX
 * ID for the centre's scanner, or no tutor register for an online batch. Called
 * out on the row and in the banner, because a row of question marks with no
 * explanation reads as a broken page rather than a setup gap.
 */

function StatCard({ icon, value, label, color }) {
  return (
    <Box
      sx={{
        p: 2,
        height: '100%',
        borderRadius: `${SX.radius.md}px`,
        background: SX.card,
        border: `1px solid ${SX.borderStrong}`,
        boxShadow: SX.shadowSoft,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <Box
          sx={{
            width: 38,
            height: 38,
            flexShrink: 0,
            borderRadius: `${SX.radius.sm}px`,
            background: `${color}14`,
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon icon={icon} width={20} height={20} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 19, fontWeight: 800, lineHeight: 1.1, color: SX.text }}>
            {value}
          </Typography>
          <Typography noWrap sx={{ fontSize: 11.5, color: SX.textMuted }} title={label}>
            {label}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

const Notice = ({ icon, text }) => (
  <Stack
    direction="row"
    spacing={1}
    alignItems="flex-start"
    sx={{
      px: 2,
      py: 1.25,
      borderRadius: `${SX.radius.sm}px`,
      background: 'rgba(194,94,0,0.07)',
      border: '1px solid rgba(194,94,0,0.22)',
    }}
  >
    <Icon icon={icon} width={17} height={17} color="#C25E00" />
    <Typography sx={{ fontSize: 12.5, color: SX.textMuted }}>{text}</Typography>
  </Stack>
);

/** What the codes in the grid mean. A grid of letters needs a key. */
const Legend = () => (
  <Stack direction="row" useFlexGap flexWrap="wrap" sx={{ gap: 1.5 }}>
    {Object.entries(CODE_META).map(([code, m]) => (
      <Stack key={code} direction="row" alignItems="center" spacing={0.75}>
        <Box
          sx={{
            minWidth: 24,
            px: 0.5,
            py: 0.25,
            borderRadius: 0.75,
            fontSize: 11.5,
            fontWeight: 800,
            color: m.color,
            background: m.bg,
            textAlign: 'center',
          }}
        >
          {code}
        </Box>
        <Typography sx={{ fontSize: 12, color: SX.textMuted }}>{m.label}</Typography>
      </Stack>
    ))}
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Box sx={{ minWidth: 24, textAlign: 'center', color: 'text.disabled', fontWeight: 800 }}>
        &middot;
      </Box>
      <Typography sx={{ fontSize: 12, color: SX.textMuted }}>No class</Typography>
    </Stack>
  </Stack>
);

export default function SkillxAttendanceRegister() {
  const dispatch = useDispatch();
  const mdDown = useResponsive('down', 'md');

  const [batch, setBatch] = useState(null);
  const [monthValue, setMonthValue] = useState(dayjs());

  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 50,
    search: '',
    sort: { field: '', direction: '' },
    // The name column is frozen by default: a 31-column grid scrolls sideways,
    // and a mark you cannot attach to a person is not attendance.
    frozenColumns: ['student_name'],
    hiddenColumns: [],
  });

  const { attendance } = useSelector((state) => state.skillx);
  const { batches, rows, meta, loading } = attendance;

  const year = monthValue.year();
  const month = monthValue.month() + 1;

  useEffect(() => {
    dispatch(getSkillxAttendanceBatches());
  }, [dispatch]);

  useEffect(() => {
    if (batch?.batchId) {
      dispatch(getSkillxAttendanceRegister({ batchId: batch.batchId, year, month }));
    }
  }, [dispatch, batch, year, month]);

  const options = useMemo(
    () =>
      (batches || []).map((b) => ({
        batchId: b.course_batch_id,
        batchName: b.course_batch_name,
        courseName: b.course_name,
        students: b.student_count,
        scheduledDays: b.scheduled_days,
      })),
    [batches],
  );

  const headers = useMemo(
    () => buildSkillxAttendanceHead(meta?.daysInMonth || 0),
    [meta],
  );

  // The whole batch is one page of rows, so filter and page here rather than
  // round-tripping the server for ten names out of nineteen.
  const filtered = useMemo(() => {
    const q = (tableState.search || '').trim().toLowerCase();
    if (!q) return rows || [];
    return (rows || []).filter(
      (r) =>
        String(r.admn_no).includes(q) ||
        (r.student_name || '').toLowerCase().includes(q) ||
        (r.skillx_id || '').toLowerCase().includes(q),
    );
  }, [rows, tableState.search]);

  const paged = useMemo(() => {
    const size = tableState.pageSize || 50;
    const start = ((tableState.page || 1) - 1) * size;
    return filtered.slice(start, start + size);
  }, [filtered, tableState.page, tableState.pageSize]);

  const stats = useMemo(() => {
    const list = rows || [];
    const withPercent = list.filter((r) => r.percent != null);
    return {
      learners: list.length,
      classDays: meta?.scheduledDays ?? 0,
      average: withPercent.length
        ? `${Math.round(withPercent.reduce((a, r) => a + r.percent, 0) / withPercent.length)}%`
        : '—',
      untracked: list.filter((r) => !r.tracked).length,
    };
  }, [rows, meta]);

  return (
    <GlobalWrapper title="SkillX Attendance Register">
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:users-group-rounded-bold-duotone"
            value={stats.learners}
            label="Learners"
            color={SX.blue}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:calendar-bold-duotone"
            value={stats.classDays}
            label="Class days this month"
            color={SX.purple}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:chart-2-bold-duotone"
            value={stats.average}
            label="Average attendance"
            color="#2E7D62"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            icon="solar:danger-triangle-bold-duotone"
            value={stats.untracked}
            label="With no attendance source"
            color="#C25E00"
          />
        </Grid>
      </Grid>

      <Stack gap={2}>
        {/* ---------- filters: batch, then year and month ---------- */}
        <Box sx={{ display: 'flex', flexDirection: mdDown ? 'column' : 'row', gap: 2 }}>
          <Autocomplete
            sx={{ flex: 1 }}
            options={options}
            value={batch}
            onChange={(e, v) => setBatch(v)}
            getOptionLabel={(o) => `${o.batchName} — ${o.courseName}`}
            isOptionEqualToValue={(o, v) => o.batchId === v.batchId}
            renderOption={(props, o) => (
              <li {...props} key={o.batchId}>
                <Stack>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{o.batchName}</Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                    {o.courseName} · {o.students} students · {o.scheduledDays} scheduled days
                  </Typography>
                </Stack>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Batch" placeholder="Choose one of your batches" />
            )}
          />
          <Box sx={{ width: mdDown ? '100%' : 260 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                views={['year', 'month']}
                openTo="month"
                label="Year and Month"
                value={monthValue}
                onChange={(v) => v && v.isValid() && setMonthValue(v)}
                renderInput={(params) => <TextField fullWidth {...params} helperText={null} />}
              />
            </LocalizationProvider>
          </Box>
        </Box>

        {/* ---------- why the grid may be empty, said plainly ---------- */}
        {batch && !loading && meta?.scheduledDays === 0 && (
          <Notice
            icon="solar:calendar-minimalistic-bold-duotone"
            text={`${meta.batch} has no class days in ${monthValue.format(
              'MMMM YYYY',
            )}, so there is nothing to mark. Class days come from the batch calendar.`}
          />
        )}
        {batch &&
          !loading &&
          meta?.scheduledDays > 0 &&
          stats.learners > 0 &&
          stats.untracked === stats.learners && (
            <Notice
              icon="solar:danger-triangle-bold-duotone"
              text={
                meta?.mode === 'ONLINE'
                  ? `No tutor has marked a session for ${meta.batch}, so every class day reads "?" — not recorded. That is a missing register, not absent learners.`
                  : `No learner on ${meta.batch} has a SkillX ID, so the scanner records cannot be matched to anyone. Every class day reads "?" — add the IDs on the SkillX Students page.`
              }
            />
          )}

        {!batch ? (
          <Box
            sx={{
              py: 6,
              textAlign: 'center',
              borderRadius: `${SX.radius.md}px`,
              border: `1px dashed ${SX.borderStrong}`,
            }}
          >
            <Icon
              icon="solar:cursor-square-bold-duotone"
              width={34}
              height={34}
              color={SX.textMuted}
            />
            <Typography sx={{ fontSize: 13.5, color: SX.textMuted, mt: 1 }}>
              Choose a batch to see its register.
            </Typography>
          </Box>
        ) : (
          <>
            <GlobalTable
              headers={headers}
              rows={paged}
              meta={{
                totalRecords: filtered.length,
                totalPages: Math.max(1, Math.ceil(filtered.length / (tableState.pageSize || 50))),
                hasNextPage:
                  (tableState.page || 1) * (tableState.pageSize || 50) < filtered.length,
                hasPrevPage: (tableState.page || 1) > 1,
              }}
              rowUniqueId="admn_no"
              loading={loading}
              tableState={tableState}
              setTableState={setTableState}
              handleRefresh={() =>
                dispatch(getSkillxAttendanceRegister({ batchId: batch.batchId, year, month }))
              }
            />

            <Legend />

            {/* The arithmetic, on the page. The four counts and the percentage
                are per learner FOR THE SELECTED MONTH, not running totals, and
                the difference matters when someone is comparing two months. */}
            <Box
              sx={{
                px: 2,
                py: 1.25,
                borderRadius: `${SX.radius.sm}px`,
                background: SX.cardSoft,
                border: `1px solid ${SX.border}`,
              }}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Icon
                  icon="solar:info-circle-bold-duotone"
                  width={17}
                  height={17}
                  color={SX.blue}
                />
                <Typography sx={{ fontSize: 12.5, color: SX.textMuted, lineHeight: 1.65 }}>
                  Every count is for <b>{monthValue.format('MMMM YYYY')} only</b>, per learner.{' '}
                  <b>Classes held</b> = Present + Half marked + Not marked. Approved leave,
                  holidays, off days and rescheduled classes are not counted as classes held, so
                  taking approved leave never counts against anyone.
                </Typography>
              </Stack>
            </Box>
          </>
        )}
      </Stack>
    </GlobalWrapper>
  );
}
